import { InteractionManager, MeasureOnSuccessCallback, Platform, View } from 'react-native';
import _throttle from 'lodash/throttle';

export type Position = {
    x: number;
    y: number;
    width: number;
    height: number;
    pageX: number;
    pageY: number;
};
type PendingViewRef = {
    ref: React.MutableRefObject<View | null>;
    callback: MeasureOnSuccessCallback;
};
type MeasureResult = {
    ref: React.MutableRefObject<View | null>;
    result: Position | null;
};

let pendingViewRefs: PendingViewRef[] = [];

const batchMeasureViewRefs = () => {
    const copied = [...pendingViewRefs];
    pendingViewRefs = [];
    const results: MeasureResult[] = [];

    if (copied.length === 0) return;

    const executeMeasurements = () => {
        const maybeBatchReturnValues = () => {
            if (results.length === copied.length) {
                results.forEach(({ ref, result }) => {
                    const targetCallback = copied.find((item) => item.ref === ref)?.callback;
                    if (targetCallback && result) {
                        targetCallback(result.x, result.y, result.width, result.height, result.pageX, result.pageY);
                    }
                });
            }
        };

        copied.forEach(({ ref }) => {
            if (ref.current) {
                ref.current.measure(
                    (x: number, y: number, width: number, height: number, pageX: number, pageY: number) => {
                        results.push({ ref, result: { x, y, width, height, pageX, pageY } });
                        maybeBatchReturnValues();
                    },
                );
            } else {
                results.push({ ref, result: null });
                maybeBatchReturnValues();
            }
        });
    };

    Platform.OS === 'web' ? executeMeasurements() : InteractionManager.runAfterInteractions(executeMeasurements);
};

const throttledBatchMeasureViewRefs = _throttle(batchMeasureViewRefs, 20);

/**
 * Calls to .measure are a bridge operation on native platforms.  Firing these one-off are very costly and causes errors
 * for excessive pending callbacks.  Firing these off synchronously/batched is the best way to handle this.
 */
export const batchMeasureViewRef = (ref: React.MutableRefObject<View | null>, callback: MeasureOnSuccessCallback) => {
    const alreadyHasRef = pendingViewRefs.some((item) => item.ref === ref);
    if (alreadyHasRef) {
        pendingViewRefs = pendingViewRefs.filter((item) => item.ref !== ref);
        pendingViewRefs.push({ ref, callback });
    } else {
        pendingViewRefs.push({ ref, callback });
        throttledBatchMeasureViewRefs();
    }
};
